// @flow

import * as R from "ramda"
import { createSelector } from "reselect"

import { getServicePackage, servicePackagePrice } from "booking/services/servicePackages/utils"
import { type ServicePackageApiPayload } from "booking/services/reservation/apis/types/servicePackageApiTypes"
import type {
  ServicePackageAction,
  ServicePackageData,
  ServicePackageStateType,
} from "booking/services/servicePackages/types"
import type { State as BookingStateType } from "booking/services/types"

const basicServicePackage: ServicePackageData = {
  packageType: "Basic",
  price: {
    amount: 0,
    currency: "EUR",
    base: 0,
    service: 0,
    serviceFlat: 0,
    merchant: 0,
  },
}
const initialState: ServicePackageStateType = {
  chosenServicePackage: "Basic",
  availableServicePackages: [basicServicePackage],
  loaded: false,
}

export type GetState = () => BookingStateType

const servicePackagesReducer = (
  state: ServicePackageStateType = initialState,
  action: ServicePackageAction,
): ServicePackageStateType => {
  switch (action.type) {
    case "SERVICE_PACKAGE_FETCH":
      return { ...state, loaded: false, availableServicePackages: [basicServicePackage] }
    case "SERVICE_PACKAGE_ERROR":
      return { ...state, loaded: true, error: true }
    case "SERVICE_PACKAGE_SAVE":
      return {
        ...state,
        loaded: true,
        availableServicePackages: [basicServicePackage, ...action.payload.availableServicePackages],
      }
    case "SERVICE_PACKAGE_CHOOSE":
      return { ...state, chosenServicePackage: action.payload.chosenServicePackage }
    default:
      return state
  }
}

export default servicePackagesReducer

// Data selectors
// ===
export const getServicePackages = (state: BookingStateType) => state.reservation.servicePackages

export const servicePackagesLoadedSelector: BookingStateType => boolean = createSelector(
  getServicePackages,
  sp => sp.loaded,
)

export const servicePackageApiFailedSelector: BookingStateType => boolean = createSelector(
  getServicePackages,
  sp => sp.error,
)
export const getChosenServicePackagePrice = ({
  reservation: {
    servicePackages: { chosenServicePackage, availableServicePackages },
  },
}: BookingStateType) =>
  servicePackagePrice(getServicePackage(chosenServicePackage, availableServicePackages))

export const allServicePackagesArePresent = ({
  reservation: { servicePackages: { availableServicePackages } } = {},
}: BookingStateType) => {
  if (!availableServicePackages) return false
  const storedSP = availableServicePackages.map(sp => sp.packageType)
  return R.equals(storedSP.sort(), ["Basic", "Plus", "Premium"].sort())
}

// Formatted for Save booking
export const servicePackagesSelector: $FlowFixMe = createSelector(
  getServicePackages,
  (stateSP: ServicePackageStateType): ServicePackageApiPayload => {
    const sp = getServicePackage(stateSP.chosenServicePackage, stateSP.availableServicePackages)
    const currency = sp?.price?.currency
    return {
      package_type: String(stateSP.chosenServicePackage).toLowerCase(),
      price: {
        amount: sp?.price?.amount || 0,
        currency: currency ? currency.toUpperCase() : "EUR",
        base: sp?.price?.base || 0,
        merchant: sp?.price?.merchant || 0,
        service: sp?.price?.service || 0,
        service_flat: sp?.price?.serviceFlat || 0,
      },
    }
  },
)
